import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch"; // ajustează dacă path-ul e altul

export default function JurnalTva() {
  const navigate = useNavigate();

  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, 0, 1));
  const [to, setTo] = useState(today);
  const [q, setQ] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ rows: [], totals_by_cota: [] });

  const formatDateString = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fmt2 = (n) =>
    Number(n ?? 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const res = await authFetch(`/rapoarte/jurnal-tva?from=${fromStr}&to=${toStr}`);
      setData({
        rows: Array.isArray(res?.rows) ? res.rows : [],
        totals_by_cota: Array.isArray(res?.totals_by_cota)
          ? res.totals_by_cota
          : [],
      });
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare jurnal TVA.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtru rapid (nr doc / partener / cui)
  const filteredRows = useMemo(() => {
    const rows = data.rows || [];
    const s = (q || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const a = String(r.doc_no ?? "").toLowerCase();
      const b = String(r.partener ?? "").toLowerCase();
      const c = String(r.cui ?? "").toLowerCase();
      return a.includes(s) || b.includes(s) || c.includes(s);
    });
  }, [data.rows, q]);

  // Grupare pe document (doc_id). Dacă vrei mai “strict”, poți grupa după (doc_date+doc_no+partener).
  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of filteredRows) {
      const k = r.doc_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    const arr = Array.from(m.entries()).map(([doc_id, rows]) => {
      rows.sort((a, b) => Number(a.cota ?? 0) - Number(b.cota ?? 0));
      return { doc_id, rows };
    });

    // sort grupuri după data + nr doc (ca în backend)
    arr.sort((A, B) => {
      const a0 = A.rows[0] || {};
      const b0 = B.rows[0] || {};
      const da = String(a0.doc_date || "");
      const db = String(b0.doc_date || "");
      if (da !== db) return da.localeCompare(db);
      return String(a0.doc_no || "").localeCompare(String(b0.doc_no || ""));
    });

    return arr;
  }, [filteredRows]);

  const totalsGeneral = useMemo(() => {
    const t = data.totals_by_cota || [];
    return {
      baza: t.reduce((s, x) => s + Number(x.baza || 0), 0),
      tva: t.reduce((s, x) => s + Number(x.tva || 0), 0),
      total: t.reduce((s, x) => s + Number(x.total || 0), 0),
    };
  }, [data.totals_by_cota]);

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Row className="align-items-end g-2 d-print-none">
          <Col xs={12} md={2}>
            <Form.Label className="mb-1">De la</Form.Label>
            <DatePicker
              selected={from}
              onChange={(date) => setFrom(date)}
              dateFormat="dd.MM.yyyy"
              className="form-control shadow-none"
            />
          </Col>
          <Col xs={12} md={2}>
            <Form.Label className="mb-1">Până la</Form.Label>
            <DatePicker
              selected={to}
              onChange={(date) => setTo(date)}
              dateFormat="dd.MM.yyyy"
              className="form-control shadow-none"
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label className="mb-1">Căutare</Form.Label>
            <Form.Control
              placeholder="Nr doc / Partener / CUI"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Col>
          <Col xs="auto" className="d-print-none">
            <Button onClick={load} disabled={busy} className="me-2">
              {busy ? "Încarc..." : "Reîncarcă"}
            </Button>
            <Button variant="outline-primary" onClick={() => window.print()} className="fw-semibold">
              <i className="bi bi-printer me-1"></i> Tipărește
            </Button>
          </Col>
          <Col xs="auto">
            <div className="text-muted small">
              {filteredRows.length} rânduri • {grouped.length} documente
            </div>
          </Col>
        </Row>

        {err && (
          <Alert variant="danger" className="mt-3 mb-0">
            {err}
          </Alert>
        )}

        <div className="mt-3">
          {busy ? (
            <div className="py-4 text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table hover bordered size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Data</th>
                  <th style={{ width: 90 }}>Nr</th>
                  <th>Partener</th>
                  <th style={{ width: 140 }}>CUI</th>
                  <th style={{ width: 70 }} className="text-end">
                    Cota
                  </th>
                  <th style={{ width: 140 }} className="text-end">
                    Bază
                  </th>
                  <th style={{ width: 140 }} className="text-end">
                    TVA
                  </th>
                  <th style={{ width: 140 }} className="text-end">
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                {grouped.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      Nu există date pentru interval / filtru.
                    </td>
                  </tr>
                ) : (
                  grouped.map(({ doc_id, rows }) => {
                    const first = rows[0] || {};
                    const subBaza = rows.reduce(
                      (s, r) => s + Number(r.baza || 0),
                      0,
                    );
                    const subTva = rows.reduce(
                      (s, r) => s + Number(r.tva || 0),
                      0,
                    );
                    const subTot = rows.reduce(
                      (s, r) => s + Number(r.total || 0),
                      0,
                    );

                    // +1 pentru rândul de subtotal
                    const span = rows.length + 1;

                    return (
                      <React.Fragment key={doc_id}>
                        {rows.map((r, idx) => (
                          <tr key={`${doc_id}-${r.cota}-${idx}`}>
                            {idx === 0 && (
                              <>
                                <td rowSpan={span}>{first.doc_date}</td>
                                <td rowSpan={span}>
                                  <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                    className="p-0"
                                    onClick={() =>
                                      navigate(`/iesire/${doc_id}`)
                                    } // ajustează ruta dacă ai altceva
                                    title="Deschide documentul"
                                  >
                                    {first.doc_no || ""}
                                  </span>
                                </td>
                                <td rowSpan={span}>{first.partener || "-"}</td>
                                <td rowSpan={span}>{first.cui || ""}</td>
                              </>
                            )}

                            <td className="text-end">{Number(r.cota ?? 0)}</td>
                            <td className="text-end">{fmt2(r.baza)}</td>
                            <td className="text-end">{fmt2(r.tva)}</td>
                            <td className="text-end">{fmt2(r.total)}</td>
                          </tr>
                        ))}

                        <tr className="table-light fw-semibold">
                          <td className="text-end">Subtotal</td>
                          <td className="text-end">{fmt2(subBaza)}</td>
                          <td className="text-end">{fmt2(subTva)}</td>
                          <td className="text-end">{fmt2(subTot)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>

              <tfoot className="table-secondary">
                {(data.totals_by_cota || []).map((t) => (
                  <tr key={`tot-${t.cota}`}>
                    <td colSpan={5} className="text-end fw-semibold">
                      Total cotă {Number(t.cota ?? 0)}%
                    </td>
                    <td className="text-end fw-semibold">{fmt2(t.baza)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.tva)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.total)}</td>
                  </tr>
                ))}

                <tr>
                  <td colSpan={5} className="text-end fw-bold">
                    TOTAL GENERAL
                  </td>
                  <td className="text-end fw-bold">
                    {fmt2(totalsGeneral.baza)}
                  </td>
                  <td className="text-end fw-bold">
                    {fmt2(totalsGeneral.tva)}
                  </td>
                  <td className="text-end fw-bold">
                    {fmt2(totalsGeneral.total)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
